import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { BarChart3, ChevronDown, ChevronUp } from "lucide-react";
import { fmt } from "./UI";

// Brand-aligned palette (teal-led, with warm/cool accents) — deliberately not the generic default.
const PALETTE = ["#0F766E", "#0A2540", "#B45309", "#be123c", "#0891b2", "#4d7c0f", "#7c3aed", "#c2410c", "#0369a1", "#a16207", "#0d9488", "#9f1239"];

function aggregate(rows, cfg) {
  const map = new Map();
  for (const r of rows) {
    let key = cfg.groupBy(r);
    if (key === null || key === undefined || key === "") continue;
    key = String(key);
    const v = cfg.value ? (Number(cfg.value(r)) || 0) : 1;
    map.set(key, (map.get(key) || 0) + v);
  }
  let data = [...map.entries()].map(([name, value]) => ({ name, value: Math.round(value) }));
  const sort = cfg.sort || "value_desc";
  if (sort === "time") data.sort((a, b) => (a.name < b.name ? -1 : 1));
  else if (sort === "label_asc") data.sort((a, b) => a.name.localeCompare(b.name));
  else if (sort === "value_asc") data.sort((a, b) => a.value - b.value);
  else data.sort((a, b) => b.value - a.value);
  if (cfg.limit && (cfg.type === "bar" || cfg.type === "donut")) data = data.slice(0, cfg.limit);
  return data;
}

function monthLabel(ym) {
  // "YYYY-MM" -> "Mon 'YY"
  const m = /^(\d{4})-(\d{2})/.exec(ym);
  if (!m) return ym;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[parseInt(m[2], 10) - 1]} '${m[1].slice(2)}`;
}

function ChartTile({ cfg, rows }) {
  const data = useMemo(() => aggregate(rows, cfg), [rows, cfg]);
  const isTime = (cfg.sort === "time");
  const tip = { contentStyle: { fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }, formatter: (v) => [fmt(v), cfg.valueLabel || "Value"] };

  return (
    <div className="surface p-4" data-testid={`chart-${cfg.id}`}>
      <div className="text-[13px] font-semibold text-slate-800">{cfg.title}</div>
      {cfg.subtitle && <div className="text-[11.5px] text-slate-400 mb-1">{cfg.subtitle}</div>}
      <div className="h-56 mt-2">
        {data.length === 0 ? (
          <div className="h-full grid place-items-center text-slate-300 text-[12px]">No data for this selection</div>
        ) : cfg.type === "donut" ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="80%" paddingAngle={2} stroke="none">
                {data.map((e, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <Tooltip {...tip} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        ) : cfg.type === "area" ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ left: -12, right: 8, top: 6 }}>
              <defs>
                <linearGradient id={`grad-${cfg.id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={cfg.color || PALETTE[0]} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={cfg.color || PALETTE[0]} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={isTime ? monthLabel : undefined} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10, fill: "#64748b" }} width={44} tickFormatter={(v) => fmt(v)} />
              <Tooltip {...tip} labelFormatter={isTime ? monthLabel : undefined} />
              <Area type="monotone" dataKey="value" stroke={cfg.color || PALETTE[0]} strokeWidth={2} fill={`url(#grad-${cfg.id})`} name={cfg.valueLabel || "Value"} />
            </AreaChart>
          </ResponsiveContainer>
        ) : ( // bar (horizontal for readable category labels)
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ left: 8, right: 12, top: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={(v) => fmt(v)} />
              <YAxis type="category" dataKey="name" width={cfg.labelWidth || 120} tick={{ fontSize: 10, fill: "#0f172a" }} />
              <Tooltip {...tip} cursor={{ fill: "#f1f5f9" }} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} name={cfg.valueLabel || "Value"}>
                {data.map((e, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

export default function ReportCharts({ rows = [], charts = [], testId = "report", title = "Visual insights" }) {
  const [open, setOpen] = useState(true);
  if (!charts.length) return null;

  return (
    <div className="surface overflow-hidden" data-testid={`${testId}-charts`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
        data-testid={`${testId}-charts-toggle`}
      >
        <BarChart3 size={16} className="text-teal-700" />
        <span className="font-semibold text-slate-800 text-[13.5px]">{title}</span>
        <span className="text-[11.5px] text-slate-400">· {rows.length ? `based on ${fmt(rows.length)} filtered records` : "no records"}</span>
        <span className="ml-auto text-slate-400">{open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
      </button>
      {open && (
        <div className="p-4 pt-1 border-t border-slate-100">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {charts.map((cfg) => <ChartTile key={cfg.id} cfg={cfg} rows={rows} />)}
          </div>
        </div>
      )}
    </div>
  );
}
